import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Dropdown,
  Radio,
  Responsive
} from 'semantic-ui-react'
// Local
import { CONDITION } from '../../../constant'
import { isSmartDisplay } from '../../common/displayUtils'
import {
  encounterPractitionerTagsEnabled,
  encounterPractitionerTagOptions
} from '../../common/currentUserUtils'
import { conditionTagOptions } from '../../common/practitionerTagUtils'
import {
  EncounterConditionDisplay,
  EncounterConditionPractitionerTagsRow,
  User
} from '../../authenticated/types'
import { RootState } from '../../../store'
import { ObjectValues } from '../../../types/types'

const SafeResponsive = Responsive as unknown as ComponentType<any>
const SafeDropdown = Dropdown as unknown as ComponentType<any>
const SafeRadio = Radio as unknown as ComponentType<any>

type DropdownOption = {
  key: string
  value: string
  text: string
}

type PhysicianConditionHeaderToolbarOwnProps = {
  condition: EncounterConditionDisplay
  mode?: string | null
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    conditions: EncounterConditionDisplay[]
  ) => void
}

type PhysicianConditionHeaderToolbarReduxProps = {
  currentUser: User
}

type PhysicianConditionHeaderToolbarProps =
  PhysicianConditionHeaderToolbarOwnProps &
  PhysicianConditionHeaderToolbarReduxProps

/**
 * Physician condition header toolbar. Provides editors for encounter condition
 * practitioner tags intended for display in the encounter condition header
 *
 * @param condition Condition entity
 * @param onConditionAction Callback for condition update.
 *        Arguments: action, Array of conditions (in this case an array with a
 *        single condition)
 * @param mode View mode: smart or undefined. Default: undefined
 */
function PhysicianConditionHeaderToolbar(
  props: PhysicianConditionHeaderToolbarProps
): JSX.Element | null {
  const {
    condition: conditionProp,
    mode: modeProp,
    onConditionAction,
    currentUser
  } = props

  const [ condition, setCondition ] = useState<EncounterConditionDisplay>(
    conditionProp
  )

  const handleChangePractitionerTag = (
    event: React.SyntheticEvent,
    data: {
      value: string
    }
  ) => {
    event.stopPropagation()
    const action = CONDITION.ACTION.UPDATE_PRACTITIONER_TAGS
    const updatedCondition: EncounterConditionDisplay = { ...condition }
    if (updatedCondition.practitionerTags) {
      updatedCondition.practitionerTags = {
        ...updatedCondition.practitionerTags,
        practitionerResponseCode: data.value
      }
    } else {
      updatedCondition.practitionerTags = {
        id: condition.id,
        tenantId: condition.tenantId,
        encounterConditionId: condition.id,
        practitionerResponseCode: data.value,
        creationDate: new Date(),
        modificationDate: new Date(),
        createdById: condition.createdById,
        modifiedById: condition.modifiedById
      } as EncounterConditionPractitionerTagsRow
    }
    setCondition(updatedCondition)
    onConditionAction(action, [ updatedCondition ])
  }

  const renderRadio = (
    conditionToRender: EncounterConditionDisplay,
    tagOptions: DropdownOption[]
  ): JSX.Element => {
    const currentTags = conditionToRender.practitionerTags
    const value = currentTags && currentTags.practitionerResponseCode
      ? currentTags.practitionerResponseCode
      : ''
    const name = `${conditionToRender.id}-practitionerResponseCode`

    return (
      <span className="physician-condition-toolbar-header">
        { tagOptions.map((option, i) => (
          <span key={ `${conditionToRender.id}-${option.key}` }>
            <SafeResponsive
              as={ SafeRadio }
              className={ i > 0 ? 'm-12px-left' : '' }
              label={ option.text }
              name={ name }
              value={ option.value }
              checked={ option.value === value }
              onChange={ handleChangePractitionerTag }
            />
          </span>
        )) }
      </span>
    )
  }

  const renderDropdown = (
    conditionToRender: EncounterConditionDisplay,
    tagOptions: DropdownOption[]
  ): JSX.Element => {
    const currentTags = conditionToRender.practitionerTags
    const value = currentTags && currentTags.practitionerResponseCode
      ? currentTags.practitionerResponseCode
      : ''

    return (
      <span className="physician-condition-toolbar-header">
        <SafeResponsive
          as={ SafeDropdown }
          selection
          placeholder="Mark As"
          onChange={ handleChangePractitionerTag }
          options={ tagOptions }
          value={ value }
        />
      </span>
    )
  }

  const renderPractitionerTags = (
    conditionToRender: EncounterConditionDisplay
  ): JSX.Element | null => {
    const tagsEnabled = encounterPractitionerTagsEnabled(currentUser)
    const tagOptions = encounterPractitionerTagOptions(currentUser)
    const buttonOptions = conditionTagOptions(conditionToRender, tagOptions)
    if (tagsEnabled === true && buttonOptions && buttonOptions.length > 0) {
      const dropdownOptions: DropdownOption[] = buttonOptions.map(opt => ({
        key: opt.id,
        value: opt.value,
        text: opt.text
      }))
      return isSmartDisplay({ mode: modeProp })
        ? renderDropdown(
          conditionToRender,
          dropdownOptions
        )
        : renderRadio(
          conditionToRender,
          dropdownOptions
        )
    }
    return null
  }

  if (condition) {
    return renderPractitionerTags(condition)
  }
  return null
}

const mapStateToProps = (state: RootState): PhysicianConditionHeaderToolbarReduxProps => ({
  currentUser: state.user
})

export default connect(
  mapStateToProps,
  {}
)(PhysicianConditionHeaderToolbar)
